/**
 * Testimonial Slider - Combined functionality
 * Handles:
 * - Avatar navigation (mobile)
 * - Infinite carousel navigation with chevrons (desktop)
 * - Grid layout detection (skips carousel logic for <=4 items)
 */
(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-testimonial-slider]";
  const CAROUSEL_SELECTOR = "[data-testimonial-carousel]";
  const CARD_SELECTOR = ".c-testimonial-slider__slide";

  // ============================================================================
  // Avatar Navigation (Mobile)
  // ============================================================================

  const setActiveState = (buttons, slides, index, nav) => {
    const shouldScrollNav = Boolean(
      nav && nav.scrollWidth > nav.clientWidth + 1
    );

    buttons.forEach((button, buttonIndex) => {
      const isActive = buttonIndex === index;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-selected", isActive ? "true" : "false");
      button.setAttribute("tabindex", isActive ? "0" : "-1");
      if (isActive) {
        button.setAttribute("aria-current", "true");
        if (shouldScrollNav) {
          button.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
          });
        }
      } else {
        button.removeAttribute("aria-current");
      }
    });

    if (slides && slides.length) {
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle("is-selected", slideIndex === index);
      });
    }
  };

  const selectSlide = (carousel, index) => {
    // Get the wrapper (scroll container on mobile)
    const wrapper = carousel?.closest(".c-testimonial-slider__carousel-wrapper");

    // Get all slides (excluding clones)
    const slides = carousel
      ? Array.from(carousel.children).filter(
          (el) => !el.classList.contains("c-testimonial-slider__slide--clone")
        )
      : [];

    const targetSlide = slides[index];
    if (!targetSlide) return;

    // Check if we're on mobile (wrapper is the scroll container)
    const isMobile = window.innerWidth < 700;

    if (isMobile && wrapper) {
      // On mobile: scrollIntoView centers the slide in the scroll container
      targetSlide.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    } else if (carousel && typeof carousel.select === "function") {
      // Use carousel's built-in select method on desktop
      carousel.select(index);
    } else {
      // Fallback: use scrollIntoView
      targetSlide.scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });
    }
  };

  const setupAvatarNavigation = (root) => {
    const carousel = root.querySelector(CAROUSEL_SELECTOR);
    const wrapper = root.querySelector(".c-testimonial-slider__carousel-wrapper");
    const buttons = Array.from(
      root.querySelectorAll("[data-testimonial-avatar]")
    );
    const nav = root.querySelector(".c-testimonial-slider__nav");
    const slides = carousel
      ? Array.from(carousel.children).filter(
          (el) => !el.classList.contains("c-testimonial-slider__slide--clone")
        )
      : [];

    if (!carousel || buttons.length === 0) {
      return;
    }

    const initialIndex =
      Number.parseInt(carousel.getAttribute("initial-index") || "0", 10) || 0;

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const index =
          Number.parseInt(button.getAttribute("data-index") || "0", 10) || 0;
        selectSlide(carousel, index);
        setActiveState(buttons, slides, index, nav);
      });
    });

    carousel.addEventListener("carousel:select", (event) => {
      const index = event.detail?.index ?? 0;
      setActiveState(buttons, slides, index, nav);
    });

    // Sync avatar state when user swipes on mobile (wrapper scroll)
    if (wrapper) {
      let scrollTimeout;
      wrapper.addEventListener("scroll", () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          // Find the slide whose center is closest to the viewport center
          const wrapperScrollLeft = wrapper.scrollLeft;
          const viewportCenter = wrapperScrollLeft + wrapper.clientWidth / 2;
          let closestIndex = 0;
          let closestDistance = Infinity;

          slides.forEach((slide, index) => {
            const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
            const distance = Math.abs(slideCenter - viewportCenter);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = index;
            }
          });

          setActiveState(buttons, slides, closestIndex, nav);
        }, 100);
      }, { passive: true });
    }

    setActiveState(buttons, slides, initialIndex, nav);
  };

  // ============================================================================
  // Infinite Carousel Navigation (Desktop)
  // ============================================================================

  class InfiniteTestimonialCarousel {
    constructor(carousel) {
      this.carousel = carousel;
      this.wrapper = carousel.closest(
        ".c-testimonial-slider__carousel-wrapper"
      );
      this.cards = Array.from(
        carousel.querySelectorAll(
          CARD_SELECTOR + ":not(.c-testimonial-slider__slide--clone)"
        )
      );
      this.prevButton = this.wrapper?.querySelector(
        ".c-testimonial-slider__chevron--prev"
      );
      this.nextButton = this.wrapper?.querySelector(
        ".c-testimonial-slider__chevron--next"
      );

      if (!this.cards.length || this.cards.length <= 4) {
        // Grid mode, skip initialization
        return;
      }

      this.isScrolling = false;
      this.cardWidth = 0;
      this.gap = 0;
      this.stepSize = 0;

      this.init();
    }

    init() {
      // Calculate step size (one card width + gap)
      this.updateDimensions();

      // Setup event listeners
      this.setupListeners();

      // Setup resize observer
      this.setupResizeObserver();

      // Update chevron states initially
      this.updateChevronStates();
    }

    updateDimensions() {
      if (this.cards.length === 0) return;

      const firstCard = this.cards[0];

      // Get card width (including margin/padding)
      const firstRect = firstCard.getBoundingClientRect();
      this.cardWidth = firstRect.width;

      // Get gap from CSS variable or computed gap
      const computedGap = getComputedStyle(this.carousel).gap;
      this.gap = computedGap ? parseFloat(computedGap) : 16;

      // Step size = one card width + gap
      this.stepSize = this.cardWidth + this.gap;
    }

    updateChevronStates() {
      const scrollLeft = this.carousel.scrollLeft;
      const scrollWidth = this.carousel.scrollWidth;
      const clientWidth = this.carousel.clientWidth;
      const maxScroll = scrollWidth - clientWidth;

      // Tolerance for floating point comparison
      const tolerance = 1;

      // Disable prev button if at start
      if (this.prevButton) {
        const isAtStart = scrollLeft <= tolerance;
        this.prevButton.disabled = isAtStart;
        this.prevButton.setAttribute(
          "aria-disabled",
          isAtStart ? "true" : "false"
        );
      }

      // Disable next button if at end
      if (this.nextButton) {
        const isAtEnd = scrollLeft >= maxScroll - tolerance;
        this.nextButton.disabled = isAtEnd;
        this.nextButton.setAttribute(
          "aria-disabled",
          isAtEnd ? "true" : "false"
        );
      }
    }

    setupListeners() {
      // Prev button
      if (this.prevButton) {
        this.prevButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.scrollPrev();
        });
      }

      // Next button
      if (this.nextButton) {
        this.nextButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.scrollNext();
        });
      }

      // Scroll event for chevron state updates
      this.carousel.addEventListener("scroll", this.handleScroll.bind(this), {
        passive: true,
      });

      // Prevent default scroll-carousel behavior
      this.carousel.addEventListener("control:prev", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.scrollPrev();
      });

      this.carousel.addEventListener("control:next", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.scrollNext();
      });
    }

    setupResizeObserver() {
      if (window.ResizeObserver) {
        const observer = new ResizeObserver(() => {
          this.updateDimensions();
          // Update chevron states after resize
          requestAnimationFrame(() => {
            this.updateChevronStates();
          });
        });
        observer.observe(this.carousel);
        if (this.cards[0]) {
          observer.observe(this.cards[0]);
        }
      }
    }

    scrollPrev() {
      if (this.isScrolling || this.prevButton?.disabled) return;

      this.isScrolling = true;
      this.updateDimensions();

      // Find current visible card
      const currentCard = this.getCurrentCard();
      if (!currentCard) {
        this.isScrolling = false;
        return;
      }

      // Get all cards (no clones)
      const allCards = Array.from(
        this.carousel.querySelectorAll(CARD_SELECTOR)
      );
      const currentIndex = allCards.indexOf(currentCard);

      // Find previous card
      const prevCard = allCards[currentIndex - 1];

      // If no previous card, we're at the start (shouldn't happen due to disabled check)
      if (!prevCard) {
        this.isScrolling = false;
        this.updateChevronStates();
        return;
      }

      // Scroll to previous card
      this.scrollToCard(prevCard, "prev");
    }

    scrollNext() {
      if (this.isScrolling || this.nextButton?.disabled) return;

      this.isScrolling = true;
      this.updateDimensions();

      // Find current visible card
      const currentCard = this.getCurrentCard();
      if (!currentCard) {
        this.isScrolling = false;
        return;
      }

      // Get all cards (no clones)
      const allCards = Array.from(
        this.carousel.querySelectorAll(CARD_SELECTOR)
      );
      const currentIndex = allCards.indexOf(currentCard);

      // Find next card
      const nextCard = allCards[currentIndex + 1];

      // If no next card, we're at the end (shouldn't happen due to disabled check)
      if (!nextCard) {
        this.isScrolling = false;
        this.updateChevronStates();
        return;
      }

      // Scroll to next card
      this.scrollToCard(nextCard, "next");
    }

    getCurrentCard() {
      // Find the card that is most visible/aligned with scroll position
      const scrollLeft = this.carousel.scrollLeft;
      const carouselLeft = this.carousel.offsetLeft || 0;
      const targetPosition = scrollLeft + carouselLeft;

      let closestCard = null;
      let closestDistance = Infinity;

      // Check all cards (no clones)
      this.carousel.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        const cardLeft = card.offsetLeft;
        const distance = Math.abs(cardLeft - targetPosition);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestCard = card;
        }
      });

      return closestCard;
    }

    scrollToCard(card, direction) {
      if (!card) {
        this.isScrolling = false;
        return;
      }

      // Use scrollIntoView with snap alignment for perfect card alignment
      // This ensures scroll-snap works correctly
      card.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });

      // Reset flag and update chevron states after animation
      setTimeout(() => {
        this.isScrolling = false;
        this.updateChevronStates();
      }, 600);
    }

    handleScroll() {
      if (this.isScrolling) return;
      this.updateChevronStates();
    }
  }

  // ============================================================================
  // Main Initialization
  // ============================================================================

  const setupSlider = (root) => {
    // Skip initialization if this is a grid layout (not a carousel)
    const grid = root.querySelector("[data-testimonial-grid]");
    if (grid) {
      return;
    }

    // Setup avatar navigation (mobile)
    setupAvatarNavigation(root);

    // Setup infinite carousel navigation (desktop)
    const carousel = root.querySelector(CAROUSEL_SELECTOR);
    if (carousel) {
      // Skip if already initialized
      if (carousel.dataset.infiniteCarouselInitialized === "true") {
        return;
      }

      // Skip if grid mode (4 or fewer cards)
      const cards = carousel.querySelectorAll(
        CARD_SELECTOR + ":not(.c-testimonial-slider__slide--clone)"
      );
      if (cards.length > 4) {
        carousel.dataset.infiniteCarouselInitialized = "true";
        new InfiniteTestimonialCarousel(carousel);
      }
    }
  };

  const initRoot = (root) => {
    if (root.dataset.testimonialSliderInitialized === "true") {
      return;
    }

    root.dataset.testimonialSliderInitialized = "true";
    setupSlider(root);
  };

  const setupAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const initMode = root.dataset.initMode || "eager";

      if (initMode === "lazy" && "IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                observer.disconnect();
                initRoot(root);
              }
            });
          },
          { rootMargin: "200px 0px" }
        );

        observer.observe(root);
        return;
      }

      initRoot(root);
    });
  };

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupAll, { once: true });
  } else {
    setupAll();
  }

  // Re-initialize on section load (for theme editor)
  document.addEventListener("shopify:section:load", (event) => {
    const root = event.target.querySelector(ROOT_SELECTOR);
    if (root) {
      delete root.dataset.testimonialSliderInitialized;
      const carousel = root.querySelector(CAROUSEL_SELECTOR);
      if (carousel) {
        delete carousel.dataset.infiniteCarouselInitialized;
      }
      initRoot(root);
    }
  });
})();
